import { FC, useId, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styles from './lorenzCurve.module.sass';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import { int } from "@/lib/format";

export interface LorenzPoint {
  /** Cumulative share of debtors (0..1), sorted ascending by exposure */
  x: number;
  /** Cumulative share of outstanding (0..1) */
  y: number;
}

export interface LorenzCurveProps {
  /** Lorenz coordinates from the calculator. First point should be (0,0). Last (1,1). */
  points: LorenzPoint[];
  /** Gini coefficient (0 = perfectly granular, 1 = single debtor) */
  gini?: number;
  /** Number of debtors represented in the curve */
  nDebtors?: number;
  /** Optional explicit height (defaults to 280) */
  height?: number;
  className?: string;
}

/**
 * LorenzCurve — credit concentration chart: cumulative share of outstanding (y)
 * vs cumulative share of debtors (x). The diagonal is a perfectly granular
 * book; the further the curve bows from it, the more concentrated.
 *
 * With fewer than 2 points it renders an empty wrapper; the parent handles the
 * empty state.
 */
export const LorenzCurve: FC<LorenzCurveProps> = ({
  points,
  gini,
  nDebtors,
  height = 280,
  className,
}) => {
  const uid = useId().replace(/:/g, '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [W, setW] = useState(600);
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null);

  // Responsive width measurement
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.round(el.clientWidth);
      if (w > 0) setW(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = height;
  const pad = { top: 18, right: 36, bottom: 36, left: 52 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;

  const safePoints = useMemo(() => {
    if (!points || points.length < 2) return [] as LorenzPoint[];
    // Ensure starts at origin and ends at (1,1)
    const arr = points.map((p) => ({
      x: Math.max(0, Math.min(1, p.x)),
      y: Math.max(0, Math.min(1, p.y)),
    }));
    if (arr[0].x !== 0 || arr[0].y !== 0) arr.unshift({ x: 0, y: 0 });
    if (arr[arr.length - 1].x !== 1) arr.push({ x: 1, y: 1 });
    return arr;
  }, [points]);

  const xPos = useCallback((x: number) => pad.left + x * iw, [iw, pad.left]);
  const yPos = useCallback((y: number) => pad.top + (1 - y) * ih, [ih, pad.top]);

  const curvePath = useMemo(() => {
    if (safePoints.length < 2) return '';
    return safePoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${xPos(p.x).toFixed(2)},${yPos(p.y).toFixed(2)}`)
      .join(' ');
  }, [safePoints, xPos, yPos]);

  const bandPath = useMemo(() => {
    if (safePoints.length < 2) return '';
    // Inequality band: along the curve, then (1,0) → (0,0) along the bottom, closed.
    const top = safePoints.map((p) => `${xPos(p.x).toFixed(2)},${yPos(p.y).toFixed(2)}`).join(' L');
    return `M${top} L${xPos(1).toFixed(2)},${yPos(0).toFixed(2)} L${xPos(0).toFixed(2)},${yPos(0).toFixed(2)} Z`;
  }, [safePoints, xPos, yPos]);

  const handleMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current;
      if (!svg || safePoints.length < 2) return;
      const rect = svg.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const xFrac = Math.max(0, Math.min(1, (mx - pad.left) / iw));
      // Linear interp on Lorenz points
      let lo = 0;
      let hi = safePoints.length - 1;
      while (lo < hi - 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (safePoints[mid].x <= xFrac) lo = mid;
        else hi = mid;
      }
      const a = safePoints[lo];
      const b = safePoints[hi];
      const t = b.x === a.x ? 0 : (xFrac - a.x) / (b.x - a.x);
      const yFrac = a.y + (b.y - a.y) * t;
      setHover({ idx: lo, x: xFrac, y: yFrac });
    },
    [safePoints, iw, pad.left],
  );

  const handleLeave = useCallback(() => setHover(null), []);

  if (W <= 0 || iw <= 0 || ih <= 0) {
    return <div ref={wrapRef} className={classNames('gt-scope', styles.wrap, className)} style={{ height }} />;
  }
  if (safePoints.length < 2) {
    return <div ref={wrapRef} className={classNames('gt-scope', styles.wrap, className)} />;
  }

  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const xTicks = [0, 0.25, 0.5, 0.75, 1];
  const fmtPct = (v: number) => `${Math.round(v * 100)}%`;

  return (
    <div ref={wrapRef} className={classNames('gt-scope', styles.wrap, className)}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={styles.svg}>
        <defs>
          <clipPath id={`${uid}-clip`}>
            <rect x={pad.left} y={pad.top} width={iw} height={ih} />
          </clipPath>
        </defs>

        {/* Y grid + labels */}
        {yTicks.map((t) => (
          <g key={`y-${t}`}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={yPos(t)}
              y2={yPos(t)}
              className={styles.grid}
            />
            <text
              x={pad.left - 10}
              y={yPos(t) + 3.5}
              textAnchor="end"
              className={styles.axisLabel}
            >
              {fmtPct(t)}
            </text>
          </g>
        ))}

        {/* X labels */}
        {xTicks.map((t) => (
          <text
            key={`x-${t}`}
            x={xPos(t)}
            y={H - pad.bottom + 18}
            textAnchor="middle"
            className={styles.axisLabel}
          >
            {fmtPct(t)}
          </text>
        ))}

        {/* Axis titles */}
        <text x={pad.left + iw / 2} y={H - 4} textAnchor="middle" className={styles.axisTitle}>
          % of debtors (sorted asc by exposure)
        </text>
        <text
          x={-pad.top - ih / 2}
          y={14}
          textAnchor="middle"
          transform="rotate(-90)"
          className={styles.axisTitle}
        >
          % of outstanding
        </text>

        {/* Inequality band */}
        <g clipPath={`url(#${uid}-clip)`}>
          <path d={bandPath} className={styles.band} />
        </g>

        {/* Equality diagonal */}
        <line
          x1={xPos(0)}
          y1={yPos(0)}
          x2={xPos(1)}
          y2={yPos(1)}
          className={styles.diagonal}
        />

        {/* Lorenz curve */}
        <g clipPath={`url(#${uid}-clip)`}>
          <path d={curvePath} className={styles.curve} />
        </g>

        {/* Endpoint marker */}
        <circle cx={xPos(1)} cy={yPos(1)} r="4" className={styles.endpoint} />

        {/* Hover crosshair + dot */}
        {hover && (
          <g>
            <line
              x1={xPos(hover.x)}
              x2={xPos(hover.x)}
              y1={pad.top}
              y2={pad.top + ih}
              className={styles.crosshair}
            />
            <circle cx={xPos(hover.x)} cy={yPos(hover.y)} r="5" className={styles.hoverDot} />
          </g>
        )}

        {/* Hit area */}
        <rect
          x={pad.left}
          y={pad.top}
          width={iw}
          height={ih}
          className={styles.hitRect}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        />
      </svg>

      {hover && (
        <ChartTooltip
          visible
          x={(svgRef.current?.getBoundingClientRect().left ?? 0) + xPos(hover.x)}
          y={(svgRef.current?.getBoundingClientRect().top ?? 0) + pad.top + 8}
          anchorBottom
          header={`Bottom ${Math.round(hover.x * 100)}% of debtors`}
          rows={[
            {
              color: 'var(--gt-accent)',
              label: 'hold',
              value: `${(hover.y * 100).toFixed(1)}% of outstanding`,
            },
          ]}
        />
      )}

      {(gini != null || nDebtors != null) && (
        <div aria-hidden="true" className={styles.kpiOverlay}>
          {gini != null && (
            <>
              <span className={styles.annotation}>Gini </span>
              <span className={styles.annotationValue}>{gini.toFixed(2)}</span>
            </>
          )}
          {nDebtors != null && (
            <div className={styles.annotation}>
              {int(nDebtors)} debtors
            </div>
          )}
        </div>
      )}
    </div>
  );
};

LorenzCurve.displayName = 'LorenzCurve';
export default LorenzCurve;
